import { Component, type ErrorInfo, type ReactNode } from 'react'

import { reportError } from '@/shared/lib/errorReporting'

import { ErrorFallback } from './ErrorFallback'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Error boundary global del FE. Captura excepciones del render tree de React
 * que de otra forma reventarían toda la app a pantalla blanca.
 *
 * <p>Estrategia:</p>
 * <ul>
 *   <li>En cuanto un componente hijo lanza, mostramos un fallback brutalist
 *       con CTA "Recargar" — el usuario vuelve al punto de partida sin
 *       quedar atrapado.</li>
 *   <li>El error se reporta via {@link reportError} con metadatos del React tree
 *       (componentStack): a New Relic si está inicializado y, en la app nativa,
 *       también a nuestro backend ({@code errorReporting}).</li>
 *   <li>También loggeamos a consola para que en local salte rápido durante
 *       desarrollo (StrictMode duplica los renders pero no las llamadas
 *       a componentDidCatch, así que no hay spam).</li>
 * </ul>
 *
 * <p>NO captura: errores en event handlers (esos los maneja el código del
 * componente), errores asíncronos en useEffect post-render, errores SSR.
 * Para esos casos hay que reportar manualmente con {@code reportError}.</p>
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[AppErrorBoundary] uncaught error in React tree:', error, info)
    reportError(error, {
      componentStack: info.componentStack ?? '',
      boundary: 'app-root',
    })
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback />
    }
    return this.props.children
  }
}
